import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  Chip,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  Badge,
  Button,
} from '@mui/material';
import {
  People,
  Store,
  Business,
  TrendingUp,
  Notifications,
  Warning,
  Info,
  CheckCircle,
  Error,
  Refresh,
  EmojiEvents,
  Star,
  Celebration,
  Edit,
  Add,
  AccessTime,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import dashboardService, { DashboardData } from '../../services/dashboardService';
import performerService, { Performer } from '../../services/performerService';
import { ROUTES } from '../../constants';
import { DashboardShimmer } from '../../components/common/Shimmer';
import { useAuth } from '../../hooks/useAuth';
import ProfileAvatar from '../../components/common/ProfileAvatar';

const DashboardPage: React.FC = () => {
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [performer, setPerformer] = useState<Performer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const data = await dashboardService.getDashboardData();
      setDashboardData(data);
      setError(null);
      
      // Delay performer API call to prevent simultaneous requests
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Fetch performer of the week
      try {
        const storeId = user?.store?.id;
        const performerData = await performerService.getPerformerOfTheWeek(storeId);
        setPerformer(performerData);
      } catch (perfError) {
        // Silently fail - performer is optional
        console.log('No performer of the week found');
        setPerformer(null);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleEditClick = () => {
    navigate(ROUTES.PERFORMERS);
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return <CheckCircle color="success" />;
      case 'error':
        return <Error color="error" />;
      case 'warning':
        return <Warning color="warning" />;
      default:
        return <Info color="info" />;
    }
  };


  if (loading) {
    return <DashboardShimmer />;
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box display="flex" justifyContent="center">
          <IconButton onClick={fetchDashboardData} color="primary">
            <Refresh />
          </IconButton>
        </Box>
      </Box>
    );
  }

  if (!dashboardData) return null;

  const { overview, storeStatistics, recentActivities, notifications, breakViolations = [] } = dashboardData;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ color: '#081122', fontWeight: 600, mb: 1 }}>
            Dashboard
          </Typography>
          <Typography variant="body1" sx={{ color: '#666666' }}>
            Welcome to Triveni Admin Panel - Overview of your system
          </Typography>
        </Box>
        <IconButton onClick={fetchDashboardData} color="primary" title="Refresh Dashboard">
          <Refresh />
        </IconButton>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  borderRadius: 2,
                  p: 1.5,
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(108, 117, 125, 0.3)',
                }}>
                  <People />
                </Box>
                <Typography variant="h6" component="div" sx={{ color: '#333333', fontWeight: 500 }}>
                  Total Users
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
                {overview.totalUsers}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666', mt: 1 }}>
                {overview.activeUsers} active
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  borderRadius: 2,
                  p: 1.5,
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(108, 117, 125, 0.3)',
                }}>
                  <Store />
                </Box>
                <Typography variant="h6" component="div" sx={{ color: '#333333', fontWeight: 500 }}>
                  Total Stores
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
                {overview.totalStores}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666', mt: 1 }}>
                Across {overview.totalClusters} clusters
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  borderRadius: 2,
                  p: 1.5,
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(108, 117, 125, 0.3)',
                }}>
                  <Business />
                </Box>
                <Typography variant="h6" component="div" sx={{ color: '#333333', fontWeight: 500 }}>
                  Total Clusters
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
                {overview.totalClusters}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666', mt: 1 }}>
                Active clusters
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease',
            }
          }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Box sx={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  borderRadius: 2,
                  p: 1.5,
                  mr: 2,
                  boxShadow: '0 4px 12px rgba(108, 117, 125, 0.3)',
                }}>
                  <TrendingUp />
                </Box>
                <Typography variant="h6" component="div" sx={{ color: '#333333', fontWeight: 500 }}>
                  Today's Attendance
                </Typography>
              </Box>
              <Typography variant="h4" sx={{ color: '#081122', fontWeight: 600 }}>
                {overview.todayAttendance}
              </Typography>
              <Typography variant="body2" sx={{ color: '#666666', mt: 1 }}>
                {overview.attendanceRate}% attendance rate
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performer of the Week Card */}
      {performer && (
        <Card sx={{
          mb: 4,
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          background: user?.id === performer.user_id
            ? 'linear-gradient(135deg, #FFB347 0%, #FF8C00 100%)'
            : 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
          color: 'white',
          position: 'relative',
        }}>
          {/* Edit Button for CLM/SUPER ADMIN */}
          {(user?.role === 'super_admin' || user?.role === 'cluster_level_manager') && (
            <IconButton
              onClick={handleEditClick}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                color: 'white',
                bgcolor: 'rgba(255,255,255,0.2)',
                '&:hover': {
                  bgcolor: 'rgba(255,255,255,0.3)',
                },
              }}
              size="small"
              title="Edit Performer"
            >
              <Edit fontSize="small" />
            </IconButton>
          )}
          <CardContent sx={{ p: 3 }}>
            {user?.id === performer.user_id ? (
              // Congratulations card for current user
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Celebration sx={{ fontSize: 28, mr: 1 }} />
                  <EmojiEvents sx={{ fontSize: 32, mr: 1 }} />
                  <Celebration sx={{ fontSize: 28 }} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <ProfileAvatar
                    imageUrl={performer.user.profile_image_url}
                    fullName={`${performer.user.first_name} ${performer.user.last_name}`}
                    firstName={performer.user.first_name}
                    lastName={performer.user.last_name}
                    sx={{ width: 64, height: 64, mr: 2, bgcolor: 'rgba(255,255,255,0.3)' }}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                      Congratulations! 🎉
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 2, opacity: 0.95 }}>
                      You are the Performer of the Week!
                    </Typography>
                    <Chip
                      icon={<Star />}
                      label={`Score: ${performer.score}`}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.3)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.4)',
                      }}
                    />
                  </Box>
                </Box>
                {performer.description && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      {performer.description}
                    </Typography>
                  </Box>
                )}
              </Box>
            ) : (
              // Regular card for other users
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Box sx={{
                    bgcolor: 'rgba(255,255,255,0.2)',
                    borderRadius: 2,
                    p: 1.5,
                    mr: 2,
                  }}>
                    <EmojiEvents sx={{ fontSize: 32 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="overline" sx={{ opacity: 0.9, fontSize: '0.75rem' }}>
                      Performer of the Week
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', my: 0.5 }}>
                      {`${performer.user.first_name} ${performer.user.last_name}`}
                    </Typography>
                    {performer.user.employee_id && (
                      <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
                        {performer.user.employee_id}
                      </Typography>
                    )}
                    {performer.store && (
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {performer.store.name}
                      </Typography>
                    )}
                    <Chip
                      icon={<Star sx={{ color: '#FFD700' }} />}
                      label={`Score: ${performer.score}`}
                      size="small"
                      sx={{
                        mt: 1,
                        bgcolor: 'rgba(255,255,255,0.25)',
                        color: 'white',
                        border: '1px solid rgba(255,255,255,0.3)',
                      }}
                    />
                  </Box>
                  <ProfileAvatar
                    imageUrl={performer.user.profile_image_url}
                    fullName={`${performer.user.first_name} ${performer.user.last_name}`}
                    firstName={performer.user.first_name}
                    lastName={performer.user.last_name}
                    sx={{ width: 64, height: 64, bgcolor: 'rgba(255,255,255,0.2)' }}
                  />
                </Box>
                {performer.description && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                      {performer.description}
                    </Typography>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* No Performer Message */}
      {!performer && (user?.role === 'super_admin' || user?.role === 'cluster_level_manager') && (
        <Card sx={{
          mb: 4,
          borderRadius: 3,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}>
          <CardContent sx={{ p: 3, textAlign: 'center' }}>
            <EmojiEvents sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
            <Typography variant="h6" sx={{ color: '#666', mb: 1 }}>
              No Performer of the Week
            </Typography>
            <Typography variant="body2" sx={{ color: '#999', mb: 2 }}>
              No performer has been announced for this week yet.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => navigate(ROUTES.PERFORMERS)}
            >
              Announce Performer
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        {/* Store Statistics */}
        <Grid item xs={12} md={8}>
          <Paper sx={{
            p: 3,
            borderRadius: 3,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
                Store Performance
              </Typography>
              <Typography 
                variant="body2" 
                sx={{ color: '#666666', cursor: 'pointer' }}
                onClick={() => navigate(ROUTES.STORES)}
              >
                View All Stores →
              </Typography>
            </Box>
            
            {storeStatistics.length > 0 ? (
              <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
                {storeStatistics.map((store) => (
                  <Card key={store.id} sx={{ mb: 2, borderRadius: 2 }}>
                    <CardContent sx={{ py: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#081122' }}>
                            {store.name} ({store.code})
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666666' }}>
                            {store.city}, {store.state} • Manager: {store.manager}
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
                            {store.attendanceRate}%
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#666666' }}>
                            {store.todayAttendance}/{store.staffCount} staff
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: '#666666', textAlign: 'center', py: 4 }}>
                No store data available
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Notifications and Recent Activities */}
        <Grid item xs={12} md={4}>
          <Grid container spacing={3}>
            {/* Notifications */}
            <Grid item xs={12}>
              <Paper sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Badge badgeContent={notifications.length} color="error">
                    <Notifications sx={{ mr: 1, color: '#6c757d' }} />
                  </Badge>
                  <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
                    Notifications
                  </Typography>
                </Box>
                
                {notifications.length > 0 ? (
                  <List sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {notifications.map((notification, index) => (
                      <React.Fragment key={notification.id}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            {getNotificationIcon(notification.type)}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {notification.title}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="body2" sx={{ color: '#666666' }}>
                                {notification.message}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < notifications.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" sx={{ color: '#666666', textAlign: 'center', py: 2 }}>
                    No notifications
                  </Typography>
                )}
              </Paper>
            </Grid>

            {/* Recent Activities */}
            <Grid item xs={12}>
              <Paper sx={{
                p: 3,
                borderRadius: 3,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}>
                <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600, mb: 2 }}>
                  Recent Activities
                </Typography>
                
                {recentActivities.length > 0 ? (
                  <List sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {recentActivities.map((activity, index) => (
                      <React.Fragment key={index}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <People sx={{ color: '#6c757d' }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" sx={{ color: '#081122' }}>
                                {activity.message}
                              </Typography>
                            }
                            secondary={
                              <Typography variant="caption" sx={{ color: '#666666' }}>
                                {activity.timestamp ? new Date(activity.timestamp).toLocaleDateString() : 'Recent'}
                              </Typography>
                            }
                          />
                        </ListItem>
                        {index < recentActivities.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" sx={{ color: '#666666', textAlign: 'center', py: 2 }}>
                    No recent activities
                  </Typography>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      {/* Break Violations Section - Only for Super Admin and CLM */}
      {(user?.role === 'super_admin' || user?.role === 'cluster_level_manager') && breakViolations.length > 0 && (
        <Grid container spacing={3} sx={{ mt: 2 }}>
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Warning sx={{ fontSize: 28, color: '#ff9800', mr: 1.5 }} />
                  <Typography variant="h6" sx={{ color: '#081122', fontWeight: 600 }}>
                    Break Time Violations
                  </Typography>
                  <Chip 
                    label={breakViolations.length} 
                    color="warning" 
                    size="small" 
                    sx={{ ml: 2 }}
                  />
                </Box>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Users who exceeded the 30-minute daily break limit
                </Alert>
                <List>
                  {breakViolations.map((violation, index) => (
                    <React.Fragment key={`${violation.user_id}-${violation.date}-${index}`}>
                      <ListItem>
                        <ListItemIcon>
                          <AccessTime sx={{ color: '#ff9800' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {violation.name}
                              </Typography>
                              <Chip 
                                label={violation.employee_id} 
                                size="small" 
                                variant="outlined"
                              />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="body2" sx={{ color: '#666666' }}>
                                Date: {new Date(violation.date).toLocaleDateString('en-IN', { 
                                  day: 'numeric', 
                                  month: 'short', 
                                  year: 'numeric' 
                                })}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#666666' }}>
                                Total Break Time: <strong style={{ color: '#ff9800' }}>{violation.total_break_time} minutes</strong>
                                {' • '}
                                Excess: <strong style={{ color: '#f44336' }}>{violation.excess_time} minutes</strong>
                                {' • '}
                                Breaks Taken: {violation.breaks_count}
                              </Typography>
                              {violation.store && (
                                <Typography variant="body2" sx={{ color: '#666666' }}>
                                  Store: {violation.store.name} ({violation.store.code})
                                </Typography>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < breakViolations.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default DashboardPage;